import React, {FC, memo, useCallback, useEffect, useRef, useState} from "react";
import {Form, FormCheckbox, FormSelect, FormTextInput, Radio, TextInput} from "@montana/shared/form";
import {Translate} from "@montana/shared/translate";
import {IShippingOption, ShippingOption} from "./shipping-option";
import {Button, ButtonStyles} from "@montana/shared/button";
import {IFormRef, IFormValues} from "@montana/shared/form/form.component";
import {
    city,
    combineValidators,
    exactLength,
    noCityMatchZipCode,
    required,
    streetNumber
} from "@montana/shared/form/validation";
import {useTranslations} from "@montana/containers/translations";
import {
    IAddCustomerInformationRequest,
    IUpdateShippingInformationRequest
} from "@montana/models/generated/cms.interface";
import {useTracking} from "@montana/containers/tracking";
import {subscribe} from '@montana/services/newsletter';
import {useSettings} from "@montana/containers/settings/settings.container";
import {useCheckout} from "@montana/containers/checkout";
import {RichText} from "@montana/shared/rich-text";
import {useConnectedServiceFunctions} from "@montana/shared/use-connected-service-functions";
import {useDynamicSettings} from "@montana/containers/dynamic-settings";
import {FormikErrors} from "formik";
import {IFieldOptionValue} from '@montana/shared/form/field/field';
import cx from 'clsx';
import {getCityMatchingZipCode} from '@montana/services/checkout';
import {useMediaQuery} from 'react-responsive';

interface IShippingOptionsProps {
    options: IShippingOption[];
    countries: IFieldOptionValue[];
    onSubmit: (form: IUpdateShippingInformationRequest, customerInformation: IAddCustomerInformationRequest) => void;
    submitForm?: boolean;
    newsletterSignupAcceptance?: string;
    termsAndPrivacyPolicy?: string;
}

function shouldScrollToError(fieldName: string, errors: FormikErrors<IFormValues>): boolean {
    return Object.keys(errors).length === 1 && Object.keys(errors).findIndex(k => fieldName === k) !== -1;
}

export const ShippingOptionsForm: FC<IShippingOptionsProps> = memo((props: IShippingOptionsProps) => {
    const { countries, options, onSubmit, submitForm, termsAndPrivacyPolicy, newsletterSignupAcceptance } = props;
    const [selectedShippingOptionId, setSelectedShipping] = useState<string>('');
    const [selectedParcelShopId, setSelectedParcelShopId] = useState<string | undefined>();
    const [showParcelShopNotSelectedMessage, setShowParcelShopNotSelectedMessage] = useState<boolean>(false);
    const { market } = useSettings();
    const { trackCustomEvent } = useTracking();
    const { customerInformation, selectedShippingInformation, setShippingInformation } = useCheckout();
    const { cfCountry } = useDynamicSettings();
    const translate = useTranslations();

    const shippingStreetField = 'shippingStreet';
    const shippingAdditionalAddressField = 'shippingAdditionalAddress';
    const shippingZipCodeField = 'shippingZipCode';
    const shippingCityField = 'shippingCity';
    const shippingCountryField = 'shippingCountry';
    const billingAddressSameAsShipping = 'billingAddressSameAsShipping';

    const [cSubscribe] = useConnectedServiceFunctions(subscribe);
    const fieldRequiredText = translate(t => t.forms.fieldRequired, "[This field is required]");

    const formRef = useRef<IFormRef>(null);

    const [disableCity, setDisableCity] = useState<boolean>(true);
    const [noMatchingCity, setNoMatchingCity] = useState<boolean>(false);
    const [cGetCityMatchingZipCode] = useConnectedServiceFunctions(getCityMatchingZipCode);
    const [showZipCodeLabel, setShowZipCodeLabel] = useState<boolean>(false);
    const isMobile = useMediaQuery({ maxWidth: 440 });


    function handleMatchZipCodeToCity(zipCode: string) {
        if (!market?.marketId || market?.checkoutZipCodeLength !== zipCode.length) {
            formRef?.current?.getForm()?.setFieldValue(shippingCityField, '');
            setDisableCity(false);
            setNoMatchingCity(true);
            formRef?.current?.getForm()?.runSingleFieldLevelValidation(shippingZipCodeField, zipCode);
            formRef?.current?.getForm()?.setFieldTouched(shippingZipCodeField, true, true);
            return false;
        }

        cGetCityMatchingZipCode(zipCode, market?.marketId)
            .then(response => {
                if (response.ok) {
                    response.json().then(city => {
                        formRef?.current?.getForm()?.setFieldValue(shippingCityField, city);
                        setDisableCity(true);
                        setNoMatchingCity(false);
                        formRef?.current?.getForm()?.runSingleFieldLevelValidation(shippingZipCodeField, zipCode);
                        formRef?.current?.getForm()?.setFieldTouched(shippingZipCodeField, true, true);
                    })
                } else {
                    response.json().then(() => {
                        setDisableCity(false);
                        setNoMatchingCity(true);
                        formRef?.current?.getForm()?.runSingleFieldLevelValidation(shippingZipCodeField, zipCode);
                        formRef?.current?.getForm()?.setFieldTouched(shippingZipCodeField, true, true);
                    })
                }
            })
            .catch(e => console.log('error', e))
    }
    useEffect(() => {
        if (!!options.length) {
            setSelectedShipping(options[0].deliveryId || '');
        } else {
            setSelectedShipping('');
        }
    }, [options]);

    useEffect(() => {
        if (submitForm) {
            formRef.current?.getForm()?.submitForm();
        }
    }, [submitForm]);

    const handleSelectParcelShop = (parcelShopId: string) => {
        setShowParcelShopNotSelectedMessage(false);
        setSelectedParcelShopId(parcelShopId);
        updateShippingOption(parcelShopId);
    }

    const getRadioOptions = useCallback(() => {
        return options?.map((option, index) => ({
            element: <ShippingOption
                option={option}
                isSelected={option.deliveryId === selectedShippingOptionId}
                onSelectParcelShop={handleSelectParcelShop}
            />,
            value: option.deliveryId || `${option.type}-${index}`
        })) ?? [];
    }, [options, selectedShippingOptionId])

    async function handleSubmit(values: IFormValues) {
        const selectedShippingOption = options.find(so => so.deliveryId === selectedShippingOptionId);

        if (selectedShippingOption?.type !== 'homeDelivery' && !selectedParcelShopId) {
            setShowParcelShopNotSelectedMessage(true);
            return false;
        }

        if (values['newsletter'] === true) {
            if (customerInformation.personalInformation && customerInformation.billingAddress) {
                await cSubscribe({
                    fullName: customerInformation.personalInformation.fullName,
                    email: customerInformation.personalInformation.email,
                    country: cfCountry || market?.marketId!,
                    customerType: 'Private customer',
                    language: market?.languageName!,
                    signupSource: 'Checkout'
                });
                trackCustomEvent('newsletter', 'sign up', document.location.pathname);
            }
        }


        const customerInfo = !values[billingAddressSameAsShipping] ? {
            ...customerInformation,
            shippingAddress: {
                street: values[shippingStreetField],
                additionalAddress: values[shippingAdditionalAddressField],
                zipCode: values[shippingZipCodeField],
                city: values[shippingCityField],
                country: countries.find(c => c.value === values['shippingCountry'])?.text || '',
                countryCode: values['shippingCountry']
            },
            billingAddressSameAsShipping: values[billingAddressSameAsShipping]
        } : {
            ...customerInformation,
            shippingAddress: {
                ...customerInformation.billingAddress
            },
            billingAddressSameAsShipping: values[billingAddressSameAsShipping]
        } as IAddCustomerInformationRequest;

        onSubmit({
            isHomeDeliverySelected: selectedShippingOption?.type === 'homeDelivery',
            glsParcelShopNumberId: selectedShippingOption?.type !== 'homeDelivery' ? selectedParcelShopId : undefined,
            shippingServiceAgentCodeKey: values['homeDeliveryOption'],
            deliveryId: selectedShippingOption?.deliveryId || '',
        }, customerInfo)
    }

    useEffect(() => {
        updateShippingOption();
    }, [selectedShippingOptionId]);

    const updateShippingOption = (parcelShopId?: string) => {
        if (!!selectedShippingOptionId) {
            const selectedShippingOption = options.find(so => so.deliveryId === selectedShippingOptionId);

            const values = formRef.current?.getForm()?.getFormikBag().values;

            if (selectedShippingOption?.type === 'homeDelivery') {
                setSelectedParcelShopId(undefined);
            }

            setShippingInformation({
                isHomeDeliverySelected: selectedShippingOption?.type === 'homeDelivery',
                glsParcelShopNumberId: selectedShippingOption?.type !== 'homeDelivery' ? (selectedParcelShopId ?? parcelShopId) : undefined,
                shippingServiceAgentCodeKey: values ? values['homeDeliveryOption'] : '',
                deliveryId: selectedShippingOption?.deliveryId || '',
            })
        }
    }

    useEffect(() => {
        if (selectedShippingInformation?.isAssemblerDelivery) {
            formRef?.current?.getForm()?.setFieldValue(billingAddressSameAsShipping, true);
        } else {
            formRef?.current?.getForm()?.setFieldValue(billingAddressSameAsShipping, customerInformation.billingAddressSameAsShipping);
        }
    }, [selectedShippingInformation])

    return (
        <section className={cx(['shipping-options-form', showParcelShopNotSelectedMessage ? 'show-parcel-shop-error' : ''])}>
            <h5 className="checkout-b2c-base__heading">
                <Translate value="checkoutPage.shippingInformationHeading" defaultValue="[Select your choice of shipping]" />
            </h5>
            {options.length > 0 &&
                <Form
                    onSubmit={handleSubmit}
                    initialValues={{}}
                    ref={formRef}>
                    {({ values, errors, submitCount }) => <>
                        <h6 className="checkout-b2c-base__sub-heading">
                            <Translate value="checkoutPage.shippingOptionsHeading" defaultValue="[Shipping options]" />
                        </h6>
                        <Radio
                            value={selectedShippingOptionId}
                            name="shipping-options"
                            className="shipping-options-form__option"
                            options={getRadioOptions()}
                            id="shipping-radio-group"
                            onCheck={(e) => setSelectedShipping(e)}
                        />

                        <FormCheckbox
                            className="checkout-customer-information__shipping-as-billing"
                            name={billingAddressSameAsShipping}
                            defaultValue={customerInformation.billingAddressSameAsShipping}
                            disabled={selectedShippingInformation?.isAssemblerDelivery}
                        >
                            <Translate
                                value="checkoutPage.personalInformationShippingAsBilling"
                                defaultValue="[The shipping address is the same as billing address]"
                            />
                        </FormCheckbox>

                        {!values[billingAddressSameAsShipping]
                            ? (
                                <div>
                                    <h6 className="checkout-b2c-base__sub-heading">
                                        <Translate
                                            value="checkoutPage.personalInformationShippingAddressHeading"
                                            defaultValue="[Shipping Address]"
                                        />
                                    </h6>
                                    <FormTextInput
                                        name={shippingStreetField}
                                        label={translate(t => t.checkoutPage.personalInformationStreet, '[Street and number:]')}
                                        validate={combineValidators(
                                            required(fieldRequiredText),
                                            streetNumber(translate(t => t.forms.invalidAddress, '[Street and number is not correct]'))
                                        )}
                                        required
                                        defaultValue={customerInformation.shippingAddress?.street}
                                    />
                                    <FormTextInput
                                        name={shippingAdditionalAddressField}
                                        label={translate(t => t.checkoutPage.personalInformationAdditionalAddress, '[Additional address:]')}
                                        required={false}
                                        defaultValue={customerInformation.shippingAddress?.additionalAddress}
                                    />
                                    <div className={cx('checkout-customer-information__columns', market?.checkoutZipCodeLength ? `form-column-size-${market?.checkoutZipCodeLength}` : '')}>
                                        <div className="checkout-customer-information__zipCode">
                                            <TextInput
                                                name="zipCode"
                                                label={!isMobile || showZipCodeLabel ? translate(t => t.checkoutPage.personalInformationZipCode, '[Zip code:]') : ''}
                                                onChange={e => {
                                                    const cleanZipCode = e.target.value;
                                                    handleMatchZipCodeToCity(cleanZipCode);
                                                    formRef?.current?.getForm()?.setFieldValue(shippingZipCodeField, cleanZipCode);

                                                    if (isMobile && cleanZipCode.length > 0) {
                                                        setShowZipCodeLabel(false);
                                                    } else {
                                                        setShowZipCodeLabel(true);
                                                    }
                                                }}
                                                onBlur={(e: any) => {
                                                    const cleanZipCode = e.target.value;
                                                    if (isMobile && !cleanZipCode.length) {
                                                        setShowZipCodeLabel(true);
                                                    }
                                                }}
                                                required
                                                defaultValue={customerInformation.billingAddress?.zipCode}
                                            />
                                            <FormTextInput
                                                name={shippingZipCodeField}
                                                defaultValue={customerInformation.shippingAddress?.zipCode}
                                                required
                                                hidden
                                                validate={
                                                    combineValidators(
                                                        required(fieldRequiredText),
                                                        exactLength(market?.checkoutZipCodeLength ?? 6, translate(t => t.forms.invalidZipCodeLength, '[Invalid zip code length]')),
                                                        noCityMatchZipCode(noMatchingCity, translate(t => t.forms.invalidNoCityMatchedZipCode, '[Didn\'t find any matching city]'))
                                                    )}
                                            />
                                        </div>
                                        <div className="checkout-customer-information__city">
                                            <FormTextInput
                                                name={shippingCityField}
                                                label={translate(t => t.checkoutPage.personalInformationCity, '[City:]')}
                                                validate={
                                                    combineValidators(
                                                        required(fieldRequiredText),
                                                        city(translate(t => t.forms.invalidCity, '[City is not correct]'))
                                                    )
                                                }
                                                disabled={disableCity}
                                                required
                                                defaultValue={customerInformation.shippingAddress?.city}
                                            />
                                        </div>
                                    </div>
                                    {/*<div className="checkout-customer-information__zipCode">
                                        <FormTextInput
                                            name={shippingZipCodeField}
                                            label={translate(t => t.checkoutPage.personalInformationZipCode, '[Zip code:]')}
                                            validate={required(fieldRequiredText)}
                                            required
                                            defaultValue={customerInformation.shippingAddress?.zipCode}
                                        />
                                    </div>
                                    <FormTextInput
                                        name={shippingCityField}
                                        label={translate(t => t.checkoutPage.personalInformationCity, '[City:]')}
                                        validate={combineValidators(
                                            required(fieldRequiredText),
                                            city(translate(t => t.forms.invalidCity, '[City is not correct]'))
                                        )}
                                        required
                                        defaultValue={customerInformation.shippingAddress?.city}
                                    />*/}
                                    <FormSelect
                                        className="checkout-customer-information__country"
                                        name={shippingCountryField}
                                        placeholder={translate(t => t.checkoutPage.personalInformationCountry, '[Select country]')}
                                        hiddenLabel
                                        options={countries}
                                        validate={required(fieldRequiredText)}
                                        required
                                        defaultValue={customerInformation.shippingAddress?.countryCode ?? countries.find(c => c.value === 'DNK')?.value ?? countries[0]?.value}
                                    />
                                </div>
                            )
                            : <></>}

                        <FormCheckbox
                            className="checkout-customer-information__shipping-as-billing"
                            name="newsletter"
                            defaultValue={false}
                        >
                            {newsletterSignupAcceptance &&
                            <RichText className="checkout-customer-information__data-protection-consent-text" text={newsletterSignupAcceptance} disableAppear translation />
                            }
                        </FormCheckbox>

                        <FormCheckbox
                            className="checkout-customer-information__data-protection-consent"
                            name="dataProtectionConsent"
                            validate={required(fieldRequiredText)}
                            scrollToError={shouldScrollToError("dataProtectionConsent", errors) && submitCount}
                        >
                            {termsAndPrivacyPolicy &&
                            <RichText className="checkout-customer-information__data-protection-consent-text" text={termsAndPrivacyPolicy} disableAppear translation />
                            }
                        </FormCheckbox>

                        <Button
                            className="checkout-b2c-base__submit-button"
                            style={ButtonStyles.Tertiary}
                            submitButton
                        >
                            <Translate
                                value="checkoutPage.shippingInformationProceedToPayment"
                                defaultValue="[Proceed to Payment method]"
                            />
                        </Button>
                    </>}
                </Form>
            }
        </section>
    );
});
